import React, { useState, useEffect, useCallback, useMemo } from "react";
import {
  Box,
  Button,
  CircularProgress,
  Paper,
  ThemeProvider,
  Typography,
} from "@material-ui/core";
import { DeleteOutline, Edit, Translate } from "@material-ui/icons";
import MaterialTable from "material-table";
import { Link, useHistory, useLocation } from "react-router-dom";
import { TableIcons } from "../../../../utils/TableIcons";
import { LocalizationTheme } from "../../../../utils/utils";
import { DisplayMessage } from "../../../../services/helpers";
import DeleteConfirmationModal from "../../../Common/Modals/DeleteConfirmationModal";
import DiagnosticFlowTranslations from "./components/DiagnosticFlowTranslations";
import { getDiagnosticFlows, deleteDiagnosticFlow } from "./services/diagnosticFlowsApiService";

export default function DiagnosticFlowsTable({ resources, hasTranslationLicence }) {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedFlowId, setSelectedFlowId] = useState(null);
  const [translateFlowId, setTranslateFlowId] = useState(null);
  const history = useHistory();
  const location = useLocation();

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const result = await getDiagnosticFlows(resources);
      if (result) {
        setData(result);
      }
    } catch (e) {
      setData([]);
    }
    setLoading(false);
  }, [resources]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  useEffect(() => {
    const refreshGrid = location.state?.refreshGrid || false;
    if (refreshGrid) {
      fetchData();
      history.replace({ pathname: location.pathname, state: {} });
    }
  }, [location, fetchData, history]);

  const handleEdit = useCallback((record) => {
    history.push(`/diagnosticflows/${record.id}`);
  }, [history]);

  const handleDelete = useCallback((id) => {
    setSelectedFlowId(id);
    setShowDeleteModal(true);
  }, []);

  const handleSubmitDelete = async () => {
    const success = await deleteDiagnosticFlow(selectedFlowId, resources);
    if (success) {
      DisplayMessage(resources["DiagnosticFlowDeletedSuccessfully"], "success");
      fetchData();
    }
    setShowDeleteModal(false);
    setSelectedFlowId(null);
  };

  const tableSettings = useMemo(
    () => ({
      columns: [
        { title: resources["Name"], field: "name" },
        {
          title: resources["Status"],
          field: "isActive",
          render: (rowData) => (
            <span
              style={{
                padding: "4px 10px",
                borderRadius: "12px",
                fontSize: "12px",
                fontWeight: 600,
                background: rowData.isActive ? "#e8f5e9" : "#fafafa",
                color: rowData.isActive ? "#2e7d32" : "#999",
              }}
            >
              {rowData.isActive ? resources["Active"] : resources["Inactive"]}
            </span>
          ),
        },
      ],
      actions: [
        {
          icon: () => <Edit color="primary" />,
          tooltip: resources["Edit"],
          onClick: (_event, rowData) => handleEdit(rowData),
        },
        {
          icon: () => <Translate color="primary" />,
          tooltip: resources["Translations"],
          onClick: (_event, rowData) => setTranslateFlowId(rowData.id),
        },
        {
          icon: () => <DeleteOutline color="error" />,
          tooltip: resources["Delete"],
          onClick: (_event, rowData) => handleDelete(rowData.id),
        },
      ],
      options: {
        emptyRowsWhenPaging: false,
        pageSize: 10,
        pageSizeOptions: [10, 15, 20],
      },
      localization: {
        header: { actions: resources["Actions"] },
        body: {
          emptyDataSourceMessage: resources["NoRecordsToBeDisplayed"],
          deleteTooltip: resources["DeleteTooltip"],
          editTooltip: resources["EditTooltip"],
        },
        toolbar: {
          searchPlaceholder: resources["Search"],
          searchTooltip: resources["Search"],
        },
        pagination: {
          firstTooltip: resources["FirstTooltip"],
          lastTooltip: resources["LastTooltip"],
          previousTooltip: resources["PreviousTooltip"],
          nextTooltip: resources["NextTooltip"],
          labelRowsPerPage: resources["LabelRowsPerPage"],
          labelRowsSelect: resources["LabelRowsSelect"],
        },
      },
    }),
    [handleEdit, handleDelete, resources]
  );

  return (
    <Box>
      <Box className="position-relative py-4">
        <Typography variant="h3">
          {resources["DiagnosticFlows"]}
        </Typography>

        <Box className={"offcentered-loader " + (loading ? "fadeIn" : "fadeOut")}>
          <CircularProgress />
        </Box>

        <Box className={!loading ? "fadeIn mt-4" : "fadeOut"}>
          <Box display="flex" justifyContent="flex-end">
            <Link
              to={{ pathname: "/diagnosticflows/new" }}
              style={{ textDecoration: "none" }}
            >
              <Button id="add-diagnostic-flow" variant="contained" color="primary">
                <i
                  className="fas fa-plus mr-2"
                  title={resources["AddNewDiagnosticFlow"]}
                />
                {resources["AddNewDiagnosticFlow"]}
              </Button>
            </Link>
          </Box>

          <Paper elevation={3} className="p-3" style={{ marginTop: "26px" }}>
            <ThemeProvider theme={LocalizationTheme()}>
              <MaterialTable
                title=""
                icons={TableIcons}
                columns={tableSettings.columns}
                data={data}
                actions={tableSettings.actions}
                options={tableSettings.options}
                localization={tableSettings.localization}
              />
            </ThemeProvider>
          </Paper>
        </Box>

        <DeleteConfirmationModal
          open={showDeleteModal}
          onClose={() => setShowDeleteModal(false)}
          onConfirm={handleSubmitDelete}
          resources={resources}
          areYouSureMessage={resources["AreYouSureYouWantToDelete"]}
        />

        <DiagnosticFlowTranslations
          open={!!translateFlowId}
          flowId={translateFlowId}
          resources={resources}
          hasTranslationLicence={hasTranslationLicence}
          onClose={() => setTranslateFlowId(null)}
        />
      </Box>
    </Box>
  );
}
